<template>
  <v-card class="rounded-lg">
    <v-toolbar color="primary" density="compact">
      <v-icon class="ml-4" size="small">mdi-school</v-icon>
      <v-toolbar-title class="text-subtitle-2 font-weight-bold ml-2">CURSOS MATRICULADOS</v-toolbar-title>
      <v-spacer />
      <v-tooltip text="Ver reglamento" location="top">
        <template v-slot:activator="{ props }">
          <v-btn icon variant="text" v-bind="props" @click="verReglamento" size="small">
            <v-icon size="24">mdi-book-open-page-variant</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Imprimir horario" location="top">
        <template v-slot:activator="{ props }">
          <v-btn icon variant="text" v-bind="props" @click="printHorario" size="small">
            <v-icon size="24">mdi-printer</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip text="Nueva matrícula" location="top">
        <template v-slot:activator="{ props }">
          <v-btn icon variant="text" v-bind="props" @click="$emit('open-dialog')" size="small">
            <v-icon size="24">mdi-plus-circle</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </v-toolbar>

    <v-card-text>
      <v-data-table :headers="headersWithActions" :items="matriculas" item-key="id" density="comfortable" hide-default-footer
        v-if="matriculas.length">
        <template v-slot:[`item.fecha`]="{ item }">
          {{ formatFecha(item.fecha) }}
        </template>

        <template v-slot:[`item.costo`]="{ item }">
          <v-chip color="primary" variant="tonal" size="small">
            S/ {{ parseFloat(item.costo).toFixed(2) }}
          </v-chip>
        </template>

        <template v-slot:[`item.pagoResumen`]="{ item }">
          <v-chip
            color="success"
            variant="flat"
            size="small"
            prepend-icon="mdi-cash-check"
          >
            {{ formatMoneda(totalPagado(item)) }} / {{ formatMoneda(item.costo) }}
          </v-chip>
        </template>

        <template v-slot:[`item.deuda`]="{ item }">
          <v-chip
            :color="saldo(item) <= 0 ? 'success' : 'warning'"
            variant="tonal"
            size="small"
            prepend-icon="mdi-cash-minus"
          >
            {{ formatMoneda(saldo(item)) }}
          </v-chip>
        </template>

        <template v-slot:[`item.visa`]="{ item }">
          {{ item.visa ? 'Sí' : 'No' }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center" style="gap: 4px; white-space: nowrap;">
            <v-tooltip text="Ver Detalles" location="top">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" density="compact" size="small" v-bind="props" @click="verClases(item)">
                  <v-icon size="22">mdi-magnify</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Editar Matrícula" location="top">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" density="compact" size="small" v-bind="props" @click="editItem(item)">
                  <v-icon size="22">mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Ver asistencias" location="top">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" density="compact" size="small" v-bind="props" @click="verAsistencias(item)">
                  <v-icon size="22">mdi-file-document-outline</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Pagos y saldo" location="top">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" density="compact" size="small" v-bind="props" @click="abrirPago(item)">
                  <v-icon size="18" color="green">mdi-cash-plus</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Eliminar Matrícula" location="top">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" density="compact" size="small" v-bind="props" @click="confirmDelete(item)">
                  <v-icon size="18" color="red">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>

      <div v-else class="text-medium-emphasis text-body-2 py-2">Este alumno aún no tiene cursos matriculados.</div>
    </v-card-text>
  </v-card>

  <!-- Diálogo fullscreen para mostrar el horario -->
  <v-dialog v-model="showPdfDialog" fullscreen persistent transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon @click="showPdfDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ pdfTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>

      <v-card-text class="pa-0" style="height: 100%;">
        <iframe v-if="pdfUrl" :src="pdfUrl" style="width: 100%; height: calc(100vh - 64px); border: none;"></iframe>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="dialogDelete" width="auto" persistent>
    <v-card max-width="400" prepend-icon="mdi-delete" title="Eliminar Matrícula"
      text="¿Está seguro de eliminar esta matrícula?">
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="blue-darken-1" text @click="closeDelete">Cancelar</v-btn>
        <v-btn color="red-darken-1" text @click="deleteItemConfirm">Eliminar</v-btn>
      </template>
    </v-card>
  </v-dialog>

  <v-dialog v-model="dialogPago" max-width="700" persistent>
    <v-card>
      <v-card-title class="text-h6 d-flex align-center ga-2">
        <v-icon color="success">mdi-cash-multiple</v-icon>
        Pagos de matrícula
      </v-card-title>
      <v-card-text>
        <div class="d-flex ga-2 flex-wrap mb-4">
          <v-chip color="primary" variant="flat" prepend-icon="mdi-cash-check">
            Pagado: {{ formatMoneda(totalPagado(matriculaSeleccionada)) }}
          </v-chip>
          <v-chip color="info" variant="tonal" prepend-icon="mdi-cash-multiple">
            Total: {{ formatMoneda(matriculaSeleccionada?.costo) }}
          </v-chip>
          <v-chip :color="saldo(matriculaSeleccionada) <= 0 ? 'success' : 'warning'" variant="tonal" prepend-icon="mdi-cash-minus">
            Saldo: {{ formatMoneda(saldo(matriculaSeleccionada)) }}
          </v-chip>
        </div>

        <v-data-table
          :headers="headersPagos"
          :items="pagosList"
          class="mb-4"
          density="compact"
          item-key="id"
          no-data-text="Aún no hay pagos registrados"
          hide-default-footer
        >
          <template v-slot:[`item.monto`]="{ item }">
            {{ formatMoneda(item.monto) }}
          </template>
          <template v-slot:[`item.fecha`]="{ item }">
            {{ formatFecha(item.fecha) || '—' }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip text="Eliminar pago" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon="mdi-delete"
                  v-bind="props"
                  :loading="pagoLoading"
                  @click="eliminarPago(item)"
                >
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>

        <v-divider class="my-2" />
        <div class="d-flex ga-2 align-center flex-wrap">
          <v-text-field
            v-model.number="pagoMonto"
            label="Agregar pago"
            type="number"
            min="0.01"
            prefix="S/ "
            :rules="[rules.monto]"
            hide-details="auto"
            class="flex-grow-1"
          />
          <v-text-field
            v-model="pagoFecha"
            label="Fecha de pago"
            type="date"
            :rules="[rules.fecha]"
            hide-details="auto"
            class="flex-grow-1"
          />
          <v-btn color="primary" :loading="pagoLoading" @click="guardarPago" prepend-icon="mdi-cash-plus">
            Registrar
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cerrarPago">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>



  <EditarMatriculaDialog
    :dialog="dialogEdit"
    :edited-item="itemToEdit"
    :alumno-id="alumnoId"
    @update:dialog="dialogEdit = $event"
    @matricula-actualizada="onEdited"
  />

  <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color">
    {{ snackbar.message }}
  </v-snackbar>
</template>

<script>
import { mapActions } from 'vuex';
import EditarMatriculaDialog from './EditarMatriculaDialog.vue';

export default {
  components: { EditarMatriculaDialog },
  props: {
    matriculas: Array,
    headers: Array,
    alumnoId: [String, Number],
  },
  emits: ['open-dialog', 'matricula-eliminada', 'matricula-editada', 'pago-registrado'],
  data() {
    return {
      showPdfDialog: false,
      pdfUrl: '',
      pdfTitle: '',
      dialogDelete: false,
      dialogEdit: false,
      itemToDelete: null,
      itemToEdit: null,
      dialogPago: false,
      pagoMonto: null,
      pagoFecha: new Date().toISOString().slice(0, 10),
      pagoLoading: false,
      matriculaSeleccionada: null,
      snackbar: { show: false, message: '', color: 'success', timeout: 3000 },
      rules: {
        monto: (v) => (!!v && v > 0) || 'Ingrese un monto válido',
        fecha: (v) => !!v || 'Ingrese una fecha',
      },
      headersPagos: [
        { title: 'Monto', value: 'monto' },
        { title: 'Fecha', value: 'fecha' },
        { title: 'Acciones', value: 'actions', align: 'center', sortable: false },
      ],
    };
  },
  computed: {
    headersWithActions() {
      return [
        ...this.headers,
        { title: 'Acciones', value: 'actions', align: 'center', sortable: false },
      ];
    },
    pagosList() {
      return this.getPagos(this.matriculaSeleccionada);
    },
  },
  methods: {
    ...mapActions('alumno', ['deleteMatricula', 'registrarPagoMatricula', 'eliminarPagoMatricula']),
    verClases(item) {
      // Redirige a la vista de programación de clases utilizando la matrícula
      this.$router.push({
        name: 'programarClases',
        params: {
          matriculaId: item.id,
        },
      });
    },

    getApiBaseUrl() {
      return (process.env.VUE_APP_API_URL || 'http://localhost').replace(/\/$/, '');
    },

    verAsistencias(item) {
      if (!item?.id) return;
      this.pdfUrl = `${this.getApiBaseUrl()}/api/alumno/${item.id}/asistencias/pdf`;
      this.pdfTitle = 'REGISTRO DE ASISTENCIAS';
      this.showPdfDialog = true;
    },

    printHorario() {
      if (!this.alumnoId) return;
      this.pdfUrl = `${this.getApiBaseUrl()}/api/alumno/${this.alumnoId}/horario/pdf`;
      this.pdfTitle = 'Horario del Alumno';
      this.showPdfDialog = true;
    },

    // Preview the student's regulations from `/api/alumno/{id}/reglamento/pdf`
    verReglamento() {
      if (!this.alumnoId) return;
      this.pdfUrl = `${this.getApiBaseUrl()}/api/alumno/${this.alumnoId}/reglamento/pdf`;
      this.pdfTitle = 'Reglamento del Alumno';
      this.showPdfDialog = true;
    },

    editItem(item) {
      this.itemToEdit = { ...item };
      this.dialogEdit = true;
    },

    closeEdit() {
      this.dialogEdit = false;
      this.itemToEdit = null;
    },

    onEdited() {
      this.closeEdit();
      this.$emit('matricula-editada');
      this.mostrarSnackbar('Matrícula actualizada');
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      if (!this.itemToDelete) return;
      this.deleteMatricula({
        alumnoId: this.alumnoId,
        matriculaId: this.itemToDelete.id,
      })
        .then(() => {
          this.dialogDelete = false;
          this.itemToDelete = null;
          this.$emit('matricula-eliminada');
          this.mostrarSnackbar('Matrícula eliminada');
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || 'Error eliminando matrícula';
          this.mostrarSnackbar(msg, 'error');
        });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.itemToDelete = null;
    },

    abrirPago(item) {
      this.matriculaSeleccionada = item;
      this.pagoMonto = null;
      this.pagoFecha = new Date().toISOString().slice(0, 10);
      this.dialogPago = true;
    },

    cerrarPago() {
      this.dialogPago = false;
      this.matriculaSeleccionada = null;
      this.pagoMonto = null;
      this.pagoLoading = false;
    },

    async guardarPago() {
      if (!this.pagoMonto || !this.pagoFecha || !this.matriculaSeleccionada) return;
      this.pagoLoading = true;
      try {
        const nuevoPago = await this.registrarPagoMatricula({
          matriculaId: this.matriculaSeleccionada.id,
          monto: this.pagoMonto,
          fecha: this.pagoFecha,
        });
        if (!Array.isArray(this.matriculaSeleccionada.pago_matriculas)) {
          this.matriculaSeleccionada.pago_matriculas = [];
        }
        this.matriculaSeleccionada.pago_matriculas.push(nuevoPago);
        this.mostrarSnackbar('Pago registrado con éxito');
        this.$emit('pago-registrado');
        this.pagoMonto = null;
        this.pagoFecha = new Date().toISOString().slice(0, 10);
      } catch (error) {
        const msg = error?.response?.data?.message || 'Error registrando el pago';
        this.mostrarSnackbar(msg, 'error');
      } finally {
        this.pagoLoading = false;
      }
    },

    async eliminarPago(pago) {
      if (!pago?.id || !this.matriculaSeleccionada) return;
      this.pagoLoading = true;
      try {
        await this.eliminarPagoMatricula(pago.id);
        this.matriculaSeleccionada.pago_matriculas = (this.matriculaSeleccionada.pago_matriculas || [])
          .filter((pm) => pm.id !== pago.id);
        this.mostrarSnackbar('Pago eliminado');
      } catch (error) {
        const msg = error?.response?.data?.message || 'Error eliminando el pago';
        this.mostrarSnackbar(msg, 'error');
      } finally {
        this.pagoLoading = false;
      }
    },

    mostrarSnackbar(msg, color = 'success') {
      this.snackbar = { show: true, message: msg, color, timeout: 3000 };
    },

    totalPagado(matricula) {
      if (!matricula?.pago_matriculas?.length) return 0;
      return matricula.pago_matriculas.reduce((sum, pm) => {
        const monto = pm?.pago?.monto ?? 0;
        return sum + Number(monto);
      }, 0);
    },

    saldo(matricula) {
      const total = Number(matricula?.costo) || 0;
      return total - this.totalPagado(matricula);
    },

    getPagos(matricula) {
      if (!matricula?.pago_matriculas) return [];
      return matricula.pago_matriculas.map((pm) => ({
        id: pm.id,
        monto: pm?.pago?.monto ?? 0,
        fecha: pm?.fecha ?? pm?.pago?.created_at ?? pm?.created_at,
      }));
    },

    formatMoneda(valor) {
      const numero = Number(valor) || 0;
      return `S/ ${numero.toFixed(2)}`;
    },

    formatFecha(date) {
      if (!date) return '';
      return new Date(date)
        .toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        .replace(/\./g, '')
        .replace(/\s/g, '-')
        .toLowerCase();
    },
  },
};
</script>
